import { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSession } from './auth/session';
import { Shell } from './layout/Shell';
import { SignIn } from './screens/SignIn';
import { Apply } from './screens/Apply';
import { Dashboard } from './screens/Dashboard';
import { Applications } from './screens/Applications';
import { Retailers } from './screens/Retailers';
import { RetailerDetail } from './screens/RetailerDetail';
import { Roles, Users } from './screens/Access';
import { BulkUpload, Collections, ProductDetail, ProductList } from './screens/catalog';
import { Audit } from './screens/Audit';
import { ForgotPassword } from './screens/ForgotPassword';
import { PortalSettings } from './screens/PortalSettings';
import { ExchangeRates } from './screens/ExchangeRates';
import { Subscriptions } from './screens/Subscriptions';
import { ManufacturingBoard } from './screens/ManufacturingBoard';
import { Analytics } from './screens/Analytics';
import { PayablesDesk } from './screens/PayablesDesk';
import { ReturnsDesk } from './screens/ReturnsDesk';
import { MyInventory } from './screens/shop/MyInventory';
import { SoldItems } from './screens/shop/SoldItems';
import { ShopReturns } from './screens/shop/Returns';
import { ShopPayables } from './screens/shop/Payables';
import { ShopPurchaseInvoiceDetail } from './screens/shop/PurchaseInvoiceDetail';
import { PurchaseInvoiceDetailScreen } from './screens/PurchaseInvoiceDetail';
import { ShopDashboard } from './screens/ShopDashboard';
import { Notifications } from './screens/Notifications';
import { AccountWindDown } from './screens/AccountWindDown';
import { Languages } from './screens/Languages';
import { Translations } from './screens/Translations';
import { ShopShell } from './layout/ShopShell';
import { ShopAccountScreen, ShopCatalog, ShopStatement, ShopVariantDetail } from './screens/Shop';
import { ShopOrder, ShopOrderReview, ShopOrders, ShopOrderDetail } from './screens/shop/Order';
import { ShopConsignmentInvoiceDetail } from './screens/shop/ConsignmentInvoice';
import { ShopConsignmentInvoices } from './screens/shop/ConsignmentInvoices';
import { Orders, OrderDetailScreen } from './screens/Orders';
import { ConsignmentInvoices } from './screens/ConsignmentInvoices';
import { CreateConsignmentInvoiceScreen } from './screens/ConsignmentInvoiceCreate';
import { ConsignmentInvoiceDetailScreen, ShipmentRedirect } from './screens/ConsignmentInvoiceDetail';
import { ChangePassword } from './screens/ChangePassword';
import { AcceptTerms } from './screens/AcceptTerms';
import { Alert, Spinner } from './ui';

/**
 * One SPA, permission-gated per branch (RL-05) and portal-scoped (RL-06).
 *
 * Routes are guarded here as well as on the server. The client guard is for the
 * person — it stops them walking into a 403 — and is never the thing that keeps
 * anyone out. The server does that, and would do it even if this file said
 * otherwise.
 */
export function App() {
  const { me, loading } = useSession();
  const [applying, setApplying] = useState(false);
  const [forgot, setForgot] = useState(false);
  if (loading) return <Spinner label="Signing you in" />;
  // The pre-auth pages are not routes: an applicant has no session, and putting
  // them in the router would mean a signed-in user could navigate to them.
  if (!me) {
    if (applying) return <Apply onBack={() => setApplying(false)} />;
    // P-04 — reached from the sign-in link. Pre-auth, so no route and no page row.
    if (forgot) return <ForgotPassword onBack={() => setForgot(false)} />;
    return <SignIn onApply={() => setApplying(true)} onForgot={() => setForgot(true)} />;
  }

  // D1's default answer, made visible: one front door, and WHO you are decides
  // which application you land in. If D1 resolves the other way, this branch
  // becomes two builds rather than two route trees.
  if (me.kind === 'retailer') {
    // ON-06 — the server refuses every other retailer route while this is
    // true (`RetailerGuard`); rendering the change-password screen in place
    // of the router, the same way the pre-auth pages above do, is the UX
    // side of that gate, not the gate itself.
    if (me.mustChangePassword) return <ChangePassword />;

    // ON-12 — same shape as the password gate above, checked next: the
    // server refuses every other retailer route while `termsAcceptedAt` is
    // null (`RetailerGuard`), this just renders the matching screen instead
    // of the router.
    // A revised set of payment terms locks the portal the same way, server-side as well.
    if (!me.termsAcceptedAt || me.paymentTermsPending) return <AcceptTerms />;

    // RL-05 — where someone lands depends on what they were granted. An Accounts
    // user has no catalog, so a fixed redirect to it would drop them on a screen
    // they cannot open, on every sign-in.
    const home = me.pages.find((p) => p.granted && p.sortOrder !== null)?.route ?? '/shop/account';
    return (
      <Routes>
        <Route element={<ShopShell />}>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/shop/dashboard" element={<ShopGuard page="R-01"><ShopDashboard /></ShopGuard>} />
          <Route path="/shop/catalog" element={<ShopGuard page="R-02"><ShopCatalog /></ShopGuard>} />
          <Route path="/shop/catalog/:code" element={<ShopGuard page="R-03"><ShopVariantDetail /></ShopGuard>} />
          <Route path="/shop/order" element={<ShopGuard page="R-04"><ShopOrder /></ShopGuard>} />
          <Route path="/shop/order/review" element={<ShopGuard page="R-05"><ShopOrderReview /></ShopGuard>} />
          <Route path="/shop/orders" element={<ShopGuard page="R-06"><ShopOrders /></ShopGuard>} />
          <Route path="/shop/orders/:id" element={<ShopGuard page="R-07"><ShopOrderDetail /></ShopGuard>} />
          <Route path="/shop/consignment-invoices" element={<ShopGuard page="R-23"><ShopConsignmentInvoices /></ShopGuard>} />
          <Route path="/shop/consignment-invoices/:id" element={<ShopGuard page="R-26"><ShopConsignmentInvoiceDetail /></ShopGuard>} />
          {/* D97 R2 (CR-58): the redirects that stood here for `/shop/purchase-invoices` and its `:id`
              are GONE, as the comment they carried said they must be. After R2 that path belongs to
              the SALE document, and a redirect would send an old bookmark to the wrong one. A
              bookmark on the old despatch path now lands on the home page, which is true: that
              document is the consignment invoice and lives at `/shop/consignment-invoices`. */}
          <Route path="/shop/inventory" element={<ShopGuard page="R-20"><MyInventory /></ShopGuard>} />
          <Route path="/shop/sold" element={<ShopGuard page="R-21"><SoldItems /></ShopGuard>} />
          <Route path="/shop/returns" element={<ShopGuard page="R-25"><ShopReturns /></ShopGuard>} />
          <Route path="/shop/statement" element={<ShopGuard page="R-16"><ShopStatement /></ShopGuard>} />
          <Route path="/shop/payables" element={<ShopGuard page="R-22"><ShopPayables /></ShopGuard>} />
          <Route path="/shop/payables/invoices/:id" element={<ShopGuard page="R-27"><ShopPurchaseInvoiceDetail /></ShopGuard>} />
          <Route path="/shop/account" element={<ShopGuard page="R-18"><ShopAccountScreen /></ShopGuard>} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Guard page="A-01"><Dashboard /></Guard>} />
        <Route path="/applications" element={<Guard page="A-02"><Applications /></Guard>} />
        <Route path="/retailers" element={<Guard page="A-03"><Retailers /></Guard>} />
        <Route path="/retailers/:id" element={<Guard page="A-04"><RetailerDetail /></Guard>} />
        <Route path="/catalog/products" element={<Guard page="A-08"><ProductList /></Guard>} />
        <Route path="/catalog/products/:code" element={<Guard page="A-09"><ProductDetail /></Guard>} />
        <Route path="/catalog/collections" element={<Guard page="A-10"><Collections /></Guard>} />
        <Route path="/catalog/bulk" element={<Guard page="A-11"><BulkUpload /></Guard>} />
        <Route path="/orders" element={<Guard page="A-14"><Orders /></Guard>} />
        <Route path="/orders/:id" element={<Guard page="A-15"><OrderDetailScreen /></Guard>} />
        <Route path="/consignment-invoices" element={<Guard page="A-18"><ConsignmentInvoices /></Guard>} />
        {/* No page ID reserved for the create or detail pages (only A-18, the
            list) — the server's `shipment.view` / `shipment.manage` checks are
            the real boundary; a client Guard here would just be an extra label. */}
        <Route path="/consignment-invoices/new" element={<CreateConsignmentInvoiceScreen />} />
        <Route path="/consignment-invoices/:id" element={<ConsignmentInvoiceDetailScreen />} />
        {/* D97 R2 (CR-58): the Admin redirects for `/purchase-invoices` and `/purchase-invoices/new`
            are GONE — see the retailer routes above. That path now belongs to the SALE document. */}
        {/* Shipments are created inside a consignment invoice now. The old paths stay so
            bookmarks and links keep working. */}
        <Route path="/shipments" element={<Navigate to="/consignment-invoices" replace />} />
        <Route path="/shipments/:id" element={<ShipmentRedirect />} />
        <Route path="/users" element={<Guard page="A-06"><Users /></Guard>} />
        <Route path="/roles" element={<Guard page="A-07"><Roles /></Guard>} />
        <Route path="/audit" element={<Guard page="A-25"><Audit /></Guard>} />
        <Route path="/portal-settings" element={<Guard page="A-36"><PortalSettings /></Guard>} />
        <Route path="/exchange-rates" element={<Guard page="A-44"><ExchangeRates /></Guard>} />
        <Route path="/subscriptions" element={<Guard page="A-41"><Subscriptions /></Guard>} />
        <Route path="/manufacturing" element={<Guard page="A-26"><ManufacturingBoard /></Guard>} />
        <Route path="/analytics" element={<Guard page="A-42"><Analytics /></Guard>} />
        <Route path="/payables" element={<Guard page="A-40"><PayablesDesk /></Guard>} />
        <Route path="/payables/invoices/:id" element={<Guard page="A-45"><PurchaseInvoiceDetailScreen /></Guard>} />
        <Route path="/returns" element={<Guard page="A-43"><ReturnsDesk /></Guard>} />
        <Route path="/notifications" element={<Guard page="A-24"><Notifications /></Guard>} />
        <Route path="/wind-down" element={<Guard page="A-27"><AccountWindDown /></Guard>} />
        <Route path="/languages" element={<Guard page="A-34"><Languages /></Guard>} />
        <Route path="/translations" element={<Guard page="A-35"><Translations /></Guard>} />
        <Route path="*" element={<Alert tone="lapse">No such screen.</Alert>} />
      </Route>
    </Routes>
  );
}

/**
 * RL-05 — a screen opens when the session carries its page ID.
 *
 * The page is named by ID, not by permission, because which permission gates a
 * screen is now data (`page.permission_code`) and this file would be a second
 * copy of it that drifts. What stays true here is the mapping every registry
 * needs and cannot hold: a page ID to the component that renders it.
 *
 * This is navigation, not authorisation. Every endpoint behind these screens is
 * guarded by permission server-side, so a page reached some other way still
 * shows a person nothing they may not see.
 */
/** RL-05 — the retailer side of `Guard`. No Super Admin exists on this side. */
/**
 * A page whose CAPABILITY this portal does not have is not refused — it is sent home.
 *
 * The distinction the server already draws: `CapabilityGuard` answers 404 ("there is no such
 * thing here") and `PermissionGuard` answers 403 ("you may not"). The menu filtered on
 * capability and the route guards did not, so a page stayed open after the portal under it
 * changed: pick Subscriptions on Australia, switch to the Gulf, and the screen carried on
 * showing — its own menu entry gone — because the PERMISSION was still held.
 *
 * Home rather than an error, because nothing is wrong. Subscriptions are not part of that
 * portal's business (D46), the person deliberately changed context, and landing on their
 * dashboard is what changing context means. `replace` keeps Back working: it would otherwise
 * return them to the page they just left and bounce again.
 */
function useHomeIfUnavailable(pg: { capability?: string | null } | undefined): boolean {
  const { offers } = useSession();
  return !!pg?.capability && !offers(pg.capability);
}

function ShopGuard({ page, children }: { page: string; children: React.ReactNode }) {
  const { me } = useSession();
  const pg = me?.kind === 'retailer' ? me.pages.find((p) => p.id === page) : undefined;
  const unavailable = useHomeIfUnavailable(pg);
  if (unavailable) return <Navigate to="/" replace />;
  if (pg?.granted) return <>{children}</>;
  return (
    <Alert tone="stop">
      <b>Not available.</b> {pg ? `${pg.name} is` : 'This section is'} not part of
      your sign-in at this account. The account owner can change that under Account.
    </Alert>
  );
}

function Guard({ page, children }: { page: string; children: React.ReactNode }) {
  const { me } = useSession();
  const pg = me?.kind === 'internal' ? me.pages.find((p) => p.id === page) : undefined;
  const unavailable = useHomeIfUnavailable(pg);
  if (unavailable) return <Navigate to="/" replace />;
  if (pg?.granted) return <>{children}</>;
  const superAdmin = me?.kind === 'internal' && me.isSuperAdmin;
  return (
    <Alert tone="stop">
      <b>Not permitted.</b> {pg ? `${pg.name} requires ` : 'This screen requires a permission '}
      {pg?.permissionCode ? <code>{pg.permissionCode}</code> : null}
      {superAdmin
        ? ' — and something is wrong, Super Admin is granted every screen.'
        : ', which your roles do not grant.'}
    </Alert>
  );
}
